import { gql } from '@urql/core';

import { Layout } from 'components/common/layout/Layout';
import {
    Player_DetailsQuery,
    Player_DetailsDocument,
    Player_DetailsQueryVariables,
    Espin_Role_Enum,
    RecordProfileViewDocument,
    RecordProfileViewMutation,
    RecordProfileViewMutationVariables,
    Espin_Profile_View_Type_Enum,
} from 'generated/spin-graphql';

import { useRouter } from 'next/router';

import React, { useEffect, useMemo } from 'react';
import { useAppState } from '../../client/providers/AppProvider';
import { useAuthenticatedMutation, useAuthenticatedQuery } from '../../client/hooks';
import { GenericFullPageLoading } from '../../components/common/GenericFullPageLoading';

import { PlayerProfileContainer } from '../../components/players/PlayerProfileContainer';
import { PlainRouterQuery } from '../../types/react.types';
import ExamplePlayer from '../player-profile-example';
import { matchExactPlayerSlug } from '../../shared/shared.utils';
import Custom404 from '../404';

gql`
    query Player_Details($slug: String!) {
        espin_player_profile(
            where: { _and: [{ slug: { _ilike: $slug } }, { user: { deleted_at: { _is_null: true } } }] }
        ) {
            ...PlayerProfile
        }
    }
`;

gql`
    mutation RecordProfileView($input: espin_profile_view_insert_input!) {
        insert_espin_profile_view_one(object: $input) {
            id
        }
    }
`;

export default function Player_Details() {
    // CSR - only open to subbed colleges!
    const router = useRouter();
    const { user } = useAppState();
    const { slug } = router.query as PlainRouterQuery;
    const { isReady, data } = useAuthenticatedQuery<Player_DetailsQuery, Player_DetailsQueryVariables>(
        { query: Player_DetailsDocument, variables: useMemo(() => ({ slug }), [slug]), pause: !!!slug },
        ['espin_player_profile'],
    );

    const [result, recordProfileView] = useAuthenticatedMutation<
        RecordProfileViewMutation,
        RecordProfileViewMutationVariables
    >(RecordProfileViewDocument);

    useEffect(() => {
        if (user && data?.espin_player_profile.length > 0 && user.role === Espin_Role_Enum.EspinCollegeUser) {
            recordProfileView({
                input: {
                    college_user_full_name: `${user?.college_profile?.first_name} ${user?.college_profile?.last_name}`,
                    college_id: user.college_profile.college.id,
                    player_user_id: data.espin_player_profile[0].user_id,
                    profile_view_type: Espin_Profile_View_Type_Enum.CollegeViewsPlayer,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isReady]);

    const matchExactSlug = useMemo(() => {
        return matchExactPlayerSlug(slug, isReady, data);
    }, [isReady, data, slug]);

    if (user?.role === Espin_Role_Enum.EspinHighschoolUser) {
        return <Custom404 />;
    }

    if (!isReady) {
        return <GenericFullPageLoading />;
    }

    if (!!!matchExactSlug) {
        // not found! show the example player profile instead
        return <ExamplePlayer />;
    }

    return (
        <Layout>
            <PlayerProfileContainer player={matchExactSlug} />
        </Layout>
    );
}
